import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Dropdown, Avatar, Button, Typography, Space, Tooltip } from 'antd';
import {
  ProjectOutlined,
  SettingOutlined,
  UserOutlined,
  TeamOutlined,
  ApiOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CalculatorOutlined,
  WalletOutlined,
  QuestionCircleOutlined,
  CloudSyncOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useAuthStore } from '../../stores/authStore';
import { fetchMyQuota } from '../../services/adminService';
import NewVersionEntry from '../common/NewVersionEntry';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

interface QuotaData {
  used: number;
  limit: number | null;
}

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [quota, setQuota] = useState<QuotaData>({ used: 0, limit: null });
  const navigate = useNavigate();
  const location = useLocation();
  const { user, clearAuth } = useAuthStore();

  const loadQuota = useCallback(async () => {
    if (!user || user.role === 'SUPER_ADMIN' || user.role === 'ADMIN') return;
    try {
      const data = await fetchMyQuota();
      setQuota(data);
    } catch (err) {
      console.error('Failed to fetch quota:', err);
    }
  }, [user]);

  // Fetch quota on mount and route changes (home page)
  useEffect(() => {
    loadQuota();
  }, [location.pathname, loadQuota]);

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const menuItems: MenuProps['items'] = React.useMemo(() => {
    const items: MenuProps['items'] = [
      {
        key: '/projects',
        icon: <ProjectOutlined />,
        label: '我的项目',
        onClick: () => navigate('/projects'),
      },
    ];

    const adminChildren: MenuProps['items'] = [];

    const hasPermission = (perm: string) => {
      if (user?.role === 'SUPER_ADMIN') return true;
      return user?.permissions?.includes(perm) || false;
    };

    if (hasPermission('model:manage') || hasPermission('model:view')) {
      adminChildren.push({
        key: '/admin/models',
        icon: <ApiOutlined />,
        label: '模型管理',
        onClick: () => navigate('/admin/models'),
      });
    }

    if (hasPermission('model:manage')) {
      adminChildren.push({
        key: '/admin/resource-repairs',
        icon: <CloudSyncOutlined />,
        label: '资源修复',
        onClick: () => navigate('/admin/resource-repairs'),
      });
    }

    if (hasPermission('preset:manage')) {
      adminChildren.push({
        key: '/admin/presets',
        icon: <ProjectOutlined />,
        label: '预设项目',
        onClick: () => navigate('/admin/presets'),
      });
    }

    if (hasPermission('user:manage') || hasPermission('user:view')) {
      adminChildren.push({
        key: '/admin/users',
        icon: <TeamOutlined />,
        label: '用户管理',
        onClick: () => navigate('/admin/users'),
      });
    }

    if (hasPermission('usage:view')) {
      adminChildren.push({
        key: '/admin/usage',
        icon: <CalculatorOutlined />,
        label: '用量预估',
        onClick: () => navigate('/admin/usage'),
      });
    }

    if (hasPermission('group:manage')) {
      adminChildren.push({
        key: '/admin/groups',
        icon: <TeamOutlined />,
        label: '用户组管理',
        onClick: () => navigate('/admin/groups'),
      });
    }

    if (adminChildren.length > 0) {
      items.push({
        key: 'admin',
        icon: <SettingOutlined />,
        label: '后台管理',
        children: adminChildren,
      });
    }

    return items;
  }, [user, navigate]);

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人信息',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          padding: '0 24px',
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #f0f0f0',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          height: 64,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              width: 32,
              height: 32,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 8,
              marginRight: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 18,
              fontWeight: 'bold',
            }}
          >
            S
          </div>
          <Text strong style={{ fontSize: 18, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            SoNow 创意中心
          </Text>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          {/* 新版入口：放在余额左侧，引导用户体验新版 */}
          <NewVersionEntry />

          {/* Quota Display */}
          {user?.role !== 'SUPER_ADMIN' && user?.role !== 'ADMIN' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tooltip title="今日配额消耗 (已用 / 限额)">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '0 12px',
                    height: 32,
                    background: '#f9fafb',
                    borderRadius: 16,
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <WalletOutlined style={{ color: '#2563eb', fontSize: 14 }} />
                  <Space size={4}>
                    <Text strong style={{ color: (quota.limit && quota.used >= quota.limit) ? '#dc2626' : 'inherit' }}>
                      {quota.used.toFixed(2)}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>/</Text>
                    <Text type="secondary">
                      {quota.limit !== null ? quota.limit.toFixed(0) : '∞'}
                    </Text>
                  </Space>
                </div>
              </Tooltip>
              <Tooltip title="使用说明">
                <QuestionCircleOutlined 
                  style={{ color: '#9ca3af', cursor: 'pointer', fontSize: 16 }} 
                  onClick={() => window.open('https://file.yoyaworld.com/creative-center/doc.pdf', '_blank')}
                />
              </Tooltip>
            </div>
          )}

          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar icon={<UserOutlined />} src={user?.avatar} />
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                <Text strong style={{ fontSize: 14 }}>
                  {user?.name}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {user?.role === 'SUPER_ADMIN' ? '超级管理员' : user?.role === 'ADMIN' ? '管理员' : '普通用户'}
                </Text>
              </div>
            </div>
          </Dropdown>
        </div>
      </Header>

      <Layout>
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          theme="light"
          style={{
            borderRight: '1px solid #f0f0f0',
            position: 'fixed',
            left: 0,
            top: 64,
            bottom: 0,
            zIndex: 90,
          }}
          trigger={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        >
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            style={{ borderRight: 0, paddingTop: 16 }}
            items={menuItems}
          />
        </Sider>
        <Layout style={{ padding: '0 24px 24px', marginLeft: collapsed ? 80 : 200, transition: 'margin-left 0.2s' }}>
          <Content
            style={{
              background: '#fff',
              padding: 24,
              borderRadius: 8,
              minHeight: 'calc(100vh - 88px)',
            }}
          >
            <Outlet />
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
